import { CreateDataSourceDialog } from '@/components/DataSources/CreateDataSourceDialog'
import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'
import { useState } from 'react'

import type { WorkspaceDataSource } from 'honeygrid-types'

interface DataSourceRecordPanelProps {
	onClose: () => void
	dataSource?: WorkspaceDataSource
	isNew?: boolean
}

export function DataSourceRecordPanel({
	onClose,
	dataSource,
	isNew = false,
}: DataSourceRecordPanelProps) {
	const [createOpen, setCreateOpen] = useState(false)

	if (isNew) {
		return (
			<div className="flex flex-col h-full">
				<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DrawerTitle>Add Data Source</DrawerTitle>
					<DrawerClose asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							className="hover:bg-primary hover:text-primary-foreground"
						>
							<X className="h-4 w-4" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				<div className="flex-1 px-4 overflow-auto">
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							Upload a CSV file or create a live data source linked to a connection.
						</p>
						<Button onClick={() => setCreateOpen(true)}>Open Create Dialog</Button>
					</div>
					<CreateDataSourceDialog open={createOpen} onClose={() => setCreateOpen(false)} />
				</div>
			</div>
		)
	}

	if (!dataSource) {
		return (
			<div className="flex flex-col h-full">
				<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DrawerTitle>Data Source</DrawerTitle>
					<DrawerClose asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							className="hover:bg-primary hover:text-primary-foreground"
						>
							<X className="h-4 w-4" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				<div className="flex-1 px-4 overflow-auto">
					<div className="text-sm text-muted-foreground">Select a data source to view details.</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>{dataSource.name}</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				<div className="space-y-6">
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label>Type</Label>
							<p className="text-sm font-medium capitalize">{dataSource.type}</p>
						</div>
						<div>
							<Label>Status</Label>
							<p className="text-sm font-medium capitalize">{dataSource.status.replace('_', ' ')}</p>
						</div>
					</div>

					{dataSource.status_message && (
						<div>
							<Label>Status Message</Label>
							<p className="text-sm text-destructive">{dataSource.status_message}</p>
						</div>
					)}

					{dataSource.file_path && (
						<div>
							<Label>File Path</Label>
							<p className="text-sm break-all text-muted-foreground">{dataSource.file_path}</p>
						</div>
					)}

					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label>Total Cells</Label>
							<p className="text-sm font-medium">{dataSource.total_cells.toLocaleString()}</p>
						</div>
						{dataSource.current_version_id && (
							<div>
								<Label>Current Version</Label>
								<p className="text-sm font-medium">v{dataSource.current_version_id}</p>
							</div>
						)}
					</div>

					{dataSource.connection_id && (
						<div>
							<Label>Connection ID</Label>
							<p className="text-sm text-muted-foreground">
								Connected to ID: {dataSource.connection_id}
							</p>
						</div>
					)}

					{dataSource.workflow_id && (
						<div>
							<Label>Workflow ID</Label>
							<p className="text-sm font-mono text-muted-foreground">{dataSource.workflow_id}</p>
						</div>
					)}

					<div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
						<div>
							<Label className="text-xs">Created</Label>
							<p>{new Date(dataSource.created_at).toLocaleString()}</p>
						</div>
						{dataSource.updated_at && (
							<div>
								<Label className="text-xs">Updated</Label>
								<p>{new Date(dataSource.updated_at).toLocaleString()}</p>
							</div>
						)}
						{dataSource.completed_at && (
							<div>
								<Label className="text-xs">Completed</Label>
								<p>{new Date(dataSource.completed_at).toLocaleString()}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
